import type { GraphData } from '../../../../utils/exportUtils';
import type { GraphConfig, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import { BuildingType, DataType } from './OverviewReport.types';
import type {
  ActivePropertiesChartData,
  ImpressionsChartData,
  PageViewsChartData,
  ProspectsChartData,
} from './OverviewReport.data';

type PreComputedChartData = {
  mfProspectsData: Array<ProspectsChartData>;
  sfProspectsData: Array<ProspectsChartData>;
  mfPageViewsData: Array<PageViewsChartData>;
  sfPageViewsData: Array<PageViewsChartData>;
  mfImpressionsData: Array<ImpressionsChartData>;
  sfImpressionsData: Array<ImpressionsChartData>;
  mfActivePropertiesData: Array<ActivePropertiesChartData>;
  sfActivePropertiesData: Array<ActivePropertiesChartData>;
  prospectsBuildingType: BuildingType;
  prospectsDisplayType: DataType;
};

export const getOverviewGraphConfigs = (): Array<GraphConfig> => [
  {
    id: 'prospects',
    label: 'Listing prospects',
  },
  { id: 'page-views', label: 'Page views' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'active-properties', label: 'Active properties' },
];

const extractProspectsData = (
  chartData: Array<ProspectsChartData>,
  buildingType: BuildingType,
  includeTotal: boolean,
  includeAverage: boolean,
): GraphData => {
  const columns: Array<string> = ['Month'];
  const exportData = chartData.map((row) => {
    const result: Record<string, string | number> = {
      Month: row.name,
    };

    if (includeTotal) {
      result['Total Direct Leads'] = row['Total direct leads'];
      result['Total Listing Web Referrals'] = row['Total listing web referrals'];
      result['Total Listing Prospects'] = row['Total direct leads'] + row['Total listing web referrals'];
      result['MTD Direct Leads'] = row['Month to date direct leads'];
      result['MTD Listing Web Referrals'] = row['Month to date listing web referrals'];
      result['MTD Listing Prospects'] = row['Month to date direct leads'] + row['Month to date listing web referrals'];
    }

    if (includeAverage) {
      result['Avg. Direct Leads'] = parseFloat(row['Avg. direct leads'].toFixed(2));
      result['Avg. Listing Web Referrals'] = parseFloat(row['Avg. listing web referrals'].toFixed(2));
      result['Avg. Listing Prospects'] = parseFloat(
        (row['Avg. direct leads'] + row['Avg. listing web referrals']).toFixed(2),
      );
      result['MTD Avg. Direct Leads'] = parseFloat(row['Month to date avg. direct leads'].toFixed(2));
      result['MTD Avg. Listing Web Referrals'] = parseFloat(row['Month to date avg. listing web referrals'].toFixed(2));
      result['MTD Avg. Listing Prospects'] = parseFloat(
        (row['Month to date avg. direct leads'] + row['Month to date avg. listing web referrals']).toFixed(2),
      );
    }

    result['Median market listing prospects'] = (row['Median market listing prospects'] ?? 0).toFixed(2);
    result['MTD Median market listing prospects'] = (row['Median market listing prospects month to date'] ?? 0).toFixed(
      2,
    );

    return result;
  });

  if (includeTotal) {
    columns.push(
      'Total Direct Leads',
      'Total Listing Web Referrals',
      'Total Listing Prospects',
      'MTD Direct Leads',
      'MTD Listing Web Referrals',
      'MTD Listing Prospects',
    );
  }
  if (includeAverage) {
    columns.push(
      'Avg. Direct Leads',
      'Avg. Listing Web Referrals',
      'Avg. Listing Prospects',
      'MTD Avg. Direct Leads',
      'MTD Avg. Listing Web Referrals',
      'MTD Avg. Listing Prospects',
    );
  }
  columns.push('Median market listing prospects', 'MTD Median market listing prospects');

  const buildingTypeStr = buildingType === BuildingType.SINGLE_FAMILY ? 'Single_Family' : 'Multifamily';
  return {
    name: `Listing_Prospects_${buildingTypeStr}`,
    data: exportData,
    columns,
  };
};

const extractPageViewsData = (chartData: Array<PageViewsChartData>, buildingType: BuildingType): GraphData => {
  const buildingTypeStr = buildingType === BuildingType.SINGLE_FAMILY ? 'Single_Family' : 'Multifamily';
  return {
    name: `Page_Views_${buildingTypeStr}`,
    data: chartData.map((row) => ({
      Month: row.name,
      'Portfolio Total': row['Portfolio total'],
      'Avg. Page Views': parseFloat(row['Avg. page views'].toFixed(2)),
      'Median market page views': (row['Median market page views'] ?? 0).toFixed(2),
      'MTD Portfolio Total': row['Month to date portfolio total'],
      'MTD Avg. Page Views': parseFloat(row['Month to date median page views'].toFixed(2)),
    })),
    columns: [
      'Month',
      'Portfolio Total',
      'Avg. Page Views',
      'Median market page views',
      'MTD Portfolio Total',
      'MTD Avg. Page Views',
    ],
  };
};

const extractImpressionsData = (chartData: Array<ImpressionsChartData>, buildingType: BuildingType): GraphData => {
  const buildingTypeStr = buildingType === BuildingType.SINGLE_FAMILY ? 'Single_Family' : 'Multifamily';
  return {
    name: `Impressions_${buildingTypeStr}`,
    data: chartData.map((row) => ({
      Month: row.name,
      'Portfolio Total': row['Portfolio total'],
      'Avg. Impressions': parseFloat(row['Avg. impressions'].toFixed(2)),
      'Median market impressions': (row['Median market impressions'] ?? 0).toFixed(2),
      'MTD Portfolio Total': row['Month to date portfolio total'],
      'MTD Avg. Impressions': parseFloat(row['Month to date median impressions'].toFixed(2)),
    })),
    columns: [
      'Month',
      'Portfolio Total',
      'Avg. Impressions',
      'Median market impressions',
      'MTD Portfolio Total',
      'MTD Avg. Impressions',
    ],
  };
};

const extractActivePropertiesData = (
  chartData: Array<ActivePropertiesChartData>,
  buildingType: BuildingType,
): GraphData => {
  const buildingTypeStr = buildingType === BuildingType.SINGLE_FAMILY ? 'Single_Family' : 'Multifamily';
  return {
    name: `Active_Properties_${buildingTypeStr}`,
    data: chartData.map((row) => ({
      Month: row.name,
      'Portfolio Total': row['Portfolio total'],
      'Market Total': row['Market total'],
      'MTD Portfolio Total': row['Month to date portfolio total'],
      'MTD Market Total': row['Month to date market total'],
    })),
    columns: ['Month', 'Portfolio Total', 'Market Total', 'MTD Portfolio Total', 'MTD Market Total'],
  };
};

export const extractOverviewData = (
  selectedGraphs: SelectedGraphs,
  preComputedData: PreComputedChartData,
  isAnyPropertySingleFamily: boolean,
  allSingleFamily: boolean,
): Array<GraphData> => {
  const graphsData: Array<GraphData> = [];

  if (selectedGraphs['prospects']) {
    const { prospectsBuildingType, prospectsDisplayType } = preComputedData;
    const includeMultifamily = prospectsBuildingType === BuildingType.MULTIFAMILY && !allSingleFamily;
    const includeSingleFamily = prospectsBuildingType === BuildingType.SINGLE_FAMILY && isAnyPropertySingleFamily;
    const includeTotal = prospectsDisplayType === DataType.PORTFOLIO_TOTAL;
    const includeAverage = prospectsDisplayType === DataType.PORTFOLIO_TOTAL;

    if (includeMultifamily) {
      graphsData.push(
        extractProspectsData(preComputedData.mfProspectsData, BuildingType.MULTIFAMILY, includeTotal, includeAverage),
      );
    }
    if (includeSingleFamily) {
      graphsData.push(
        extractProspectsData(preComputedData.sfProspectsData, BuildingType.SINGLE_FAMILY, includeTotal, includeAverage),
      );
    }
  }

  if (selectedGraphs['page-views']) {
    if (!allSingleFamily) {
      graphsData.push(extractPageViewsData(preComputedData.mfPageViewsData, BuildingType.MULTIFAMILY));
    }
    if (isAnyPropertySingleFamily) {
      graphsData.push(extractPageViewsData(preComputedData.sfPageViewsData, BuildingType.SINGLE_FAMILY));
    }
  }

  if (selectedGraphs['impressions']) {
    if (!allSingleFamily) {
      graphsData.push(extractImpressionsData(preComputedData.mfImpressionsData, BuildingType.MULTIFAMILY));
    }
    if (isAnyPropertySingleFamily) {
      graphsData.push(extractImpressionsData(preComputedData.sfImpressionsData, BuildingType.SINGLE_FAMILY));
    }
  }

  if (selectedGraphs['active-properties']) {
    if (!allSingleFamily) {
      graphsData.push(extractActivePropertiesData(preComputedData.mfActivePropertiesData, BuildingType.MULTIFAMILY));
    }
    if (isAnyPropertySingleFamily) {
      graphsData.push(extractActivePropertiesData(preComputedData.sfActivePropertiesData, BuildingType.SINGLE_FAMILY));
    }
  }

  return graphsData;
};
